import { User } from '@beconfident/api-lib';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import posthog from 'posthog-js';
import { RouteLocationNormalized } from 'vue-router';

import logger from '@/infra/logger';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { EventName } from '@/types/events';

import Environment from '../environment';

interface TrackedData {
  userId: string;
  email: string | null | undefined;
  whatsapp: string;
  purchaseStatus: string;
  name: string | null | undefined;
}

class Tracking {
  private isEnabled: boolean;

  constructor(enableAnalytics: string) {
    this.isEnabled = Boolean(enableAnalytics);
  }

  init(): void {
    if (!this.isEnabled) {
      return;
    }

    posthog.init(Environment.POSTHOG_ID, {
      api_host: 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: false,
    });
  }

  async trackEvent(name: EventName, params?: Record<string, any>) {
    if (!this.isEnabled) {
      return;
    }

    const appStore = useAppStore();
    const authStore = useAuthStore();

    const eventParams = {
      ...params,
      platform: appStore.platform,
      buildVersion: appStore.info.build,
      appVersion: appStore.info.version,
      ...(authStore.user?.whatsapp && { distinct_id: authStore.user.whatsapp }),
    };

    // all tracking services here
    const promises = [
      posthog.capture(name, eventParams),

      FirebaseAnalytics.logEvent({
        name,
        params: eventParams,
      }),

      FacebookLogin.logEvent({ eventName: name }),
    ];

    try {
      /** Add allSettled polyfill for some reason */
      if (typeof Promise.allSettled !== 'function') {
        Promise.allSettled = <T>(
          promises: Iterable<T | PromiseLike<T>>,
        ): Promise<PromiseSettledResult<Awaited<T>>[]> =>
          Promise.all(
            [...promises].map((promise) =>
              Promise.resolve(promise)
                .then((value) => ({
                  status: 'fulfilled' as const,
                  value,
                }))
                .catch((error) => ({
                  status: 'rejected' as const,
                  reason: error,
                })),
            ),
          );
      }

      await Promise.allSettled(promises);
    } catch (error) {
      logger.captureException(error);

      return error;
    }
  }

  reset(): void {
    try {
      posthog.reset();
    } catch (error) {
      logger.captureException(error);
    }
  }

  private setCurrentUser(userData: TrackedData, distinctId: string): void {
    if (!this.isEnabled) {
      return;
    }

    const { userId, email, whatsapp, name, purchaseStatus } = userData;

    posthog.register({
      userId,
      email,
      whatsapp,
      purchaseStatus,
      name,
    });

    posthog.identify(distinctId, {
      userId,
      email,
      whatsapp,
      purchaseStatus,
      name,
    });
  }

  onSignIn(user: User | undefined): void {
    if (!user || !this.isEnabled) {
      return;
    }

    const purchaseStatus = user.purchase?.status;

    const distinctId = user.whatsapp;
    const trackedData: TrackedData = {
      userId: user._id,
      email: user.email,
      whatsapp: user.whatsapp,
      purchaseStatus,
      name: user.fullName,
    };

    this.setCurrentUser(trackedData, distinctId);

    this.trackEvent(EventName.SignIn, {
      user,
    });
  }

  onRouteChange(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    if (!this.isEnabled) {
      return;
    }

    posthog.capture('$pageleave', {
      $current_url: window.location.host + from.fullPath,
      path: from.fullPath,
    });
    posthog.capture('$pageview', { to: to.fullPath });

    this.trackEvent(EventName.PageView);
  }
}

export const tracking = new Tracking(Environment.ENABLE_ANALYTICS_EVENTS);
